import { WysiwygEditorStateContext } from '@src/contexts/wysiwyg/editor';
import type { TPoint, TRect, TSize } from '@src/types/misc';
import { useContext } from 'react';
import _clamp from 'lodash/clamp';

type MeasurementType = 'px' | '%';

interface Config {
  dimensions: TSize;
  hopefulLocation: TPoint;
  origin: TPoint;
  dimensionsMeasurementType?: MeasurementType;
  locationMeasurementType?: MeasurementType;
  originMeasurementType?: MeasurementType;
  returnMeasurementType?: MeasurementType;
}

type Generator = (config: Config) => TPoint;

function createPercentageAndPixelValues(
  renderRect: TRect,
  suppliedDimensions: { xAxis: number; yAxis: number },
  dimensionsArePx: boolean
) {
  const xAxisPx = dimensionsArePx
    ? suppliedDimensions.xAxis
    : suppliedDimensions.xAxis / renderRect.width;
  const yAxisPx = dimensionsArePx
    ? suppliedDimensions.yAxis
    : suppliedDimensions.yAxis / renderRect.height;
  const xAxisPct = dimensionsArePx
    ? suppliedDimensions.xAxis / renderRect.width
    : suppliedDimensions.xAxis;
  const yAxisPct = dimensionsArePx
    ? suppliedDimensions.yAxis / renderRect.height
    : suppliedDimensions.yAxis;

  return { xAxisPx, yAxisPx, xAxisPct, yAxisPct };
}

function useConstrainedPageItemLocation(): Generator {
  const { snapEdges, renderRect } = useContext(WysiwygEditorStateContext);

  return function constrainedPageItemLocation({
    dimensions,
    hopefulLocation = { x: 0, y: 0 },
    origin = { x: 0, y: 0 },
    dimensionsMeasurementType = 'px',
    locationMeasurementType = 'px',
    originMeasurementType = '%',
    returnMeasurementType = '%'
  }: Config): TPoint {
    const {
      xAxisPct: widthPercentage,
      yAxisPct: heightPercentage
    } = createPercentageAndPixelValues(
      renderRect,
      { xAxis: dimensions.width, yAxis: dimensions.height },
      dimensionsMeasurementType === 'px'
    );

    const {
      xAxisPct: xPercentage,
      yAxisPct: yPercentage
    } = createPercentageAndPixelValues(
      renderRect,
      { xAxis: hopefulLocation.x, yAxis: hopefulLocation.y },
      locationMeasurementType === 'px'
    );

    const {
      xAxisPct: originXPercentage,
      yAxisPct: originYPercentage
    } = createPercentageAndPixelValues(
      renderRect,
      { xAxis: origin.x, yAxis: origin.y },
      originMeasurementType === 'px'
    );

    const perfectX = xPercentage - widthPercentage * originXPercentage;
    const perfectY = yPercentage - heightPercentage * originYPercentage;

    const x = _clamp(
      perfectX,
      snapEdges.left,
      snapEdges.right - widthPercentage
    );
    const y = _clamp(
      perfectY,
      snapEdges.top,
      snapEdges.bottom - heightPercentage
    );

    const location = { x, y };

    if (returnMeasurementType === 'px') {
      location.x = location.x * renderRect.width;
      location.y = location.y * renderRect.height;
    }

    return location;
  };
}

export default useConstrainedPageItemLocation;
